import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Briefcase, ExternalLink, Copy, Check, Sparkles, Plus, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EngagementsListPage() {
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState(null);
  const [error, setError] = useState('');
  const [copiedToken, setCopiedToken] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '', client_company: '', country_focus: 'Mainland China',
    cohort_size: 250, cadence: 'bimonthly',
  });

  const loadEngagements = async () => {
    try {
      const r = await axios.get(`${API}/engagements`, { withCredentials: true });
      setEngagements(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load engagements');
    }
  };

  useEffect(() => { loadEngagements(); }, []);

  const copyShareLink = (token) => {
    const url = `${window.location.origin}/engagement-status/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(''), 2000);
  };

  const seedViatris = async () => {
    if (!window.confirm('Seed the Viatris × Mainland China engagement and auto-shortlist all candidate assets? This will create one engagement and run an auto-shortlist on every China-based biotech asset in your DB. Safe to re-run.')) return;
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/engagements/seed-viatris`, {}, { withCredentials: true });
      const sl = r.data?.shortlist || {};
      toast.success(`Viatris seeded — ${sl.added || 0} assets added, ${sl.active_cohort || 0} in active cohort`);
      await loadEngagements();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to seed Viatris engagement');
    }
    setSeeding(false);
  };

  const createEngagement = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.client_company.trim()) {
      toast.error('Name and client company are required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/engagements`, {
        ...form,
        cohort_size: Number(form.cohort_size) || 250,
      }, { withCredentials: true });
      toast.success('Engagement created');
      setShowCreate(false);
      setForm({ name: '', client_company: '', country_focus: 'Mainland China', cohort_size: 250, cadence: 'bimonthly' });
      await loadEngagements();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create engagement');
    }
    setCreating(false);
  };

  if (error) {
    return <div className="p-8 text-rose-600 text-sm" data-testid="engagements-error">{error}</div>;
  }
  if (!engagements) {
    return (
      <div className="p-12 flex justify-center" data-testid="engagements-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="engagements-list-page">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Briefcase className="w-6 h-6 text-slate-700" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Clients
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Every client mandate — both standing partners and active consulting engagements.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button onClick={() => setShowCreate(s => !s)} variant="outline" size="sm" data-testid="engagement-new-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Engagement
          </Button>
          {engagements.length === 0 && (
            <Button onClick={seedViatris} disabled={seeding} size="sm" className="bg-violet-600 hover:bg-violet-700" data-testid="engagement-seed-viatris-btn">
              {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              Seed Viatris
            </Button>
          )}
        </div>
      </div>

      {showCreate && (
        <Card className="p-5 mb-4 border-violet-100 bg-violet-50/30" data-testid="engagement-create-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">New Engagement</h3>
            <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-700" data-testid="engagement-create-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={createEngagement} className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Engagement Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Viatris × Mainland China Asset Intelligence" data-testid="engagement-form-name" />
            </div>
            <div>
              <Label className="text-xs">Client Company</Label>
              <Input value={form.client_company} onChange={e => setForm(p => ({ ...p, client_company: e.target.value }))} placeholder="e.g. Viatris" data-testid="engagement-form-client" />
            </div>
            <div>
              <Label className="text-xs">Country / Geography Focus</Label>
              <Input value={form.country_focus} onChange={e => setForm(p => ({ ...p, country_focus: e.target.value }))} data-testid="engagement-form-country" />
            </div>
            <div>
              <Label className="text-xs">Cohort Size</Label>
              <Input type="number" value={form.cohort_size} onChange={e => setForm(p => ({ ...p, cohort_size: e.target.value }))} data-testid="engagement-form-cohort" />
            </div>
            <div>
              <Label className="text-xs">Cadence</Label>
              <Input value={form.cadence} onChange={e => setForm(p => ({ ...p, cadence: e.target.value }))} placeholder="bimonthly / monthly / quarterly" data-testid="engagement-form-cadence" />
            </div>
            <div className="col-span-2 flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} size="sm" className="bg-slate-900" data-testid="engagement-form-submit">
                {creating && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                Create
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="space-y-3">
        {/* Standing partner — Abacus Medicine. Always shown, even before
            any engagements are seeded. Lives at /abacus, not as an
            engagement detail. */}
        <Card className="p-5 hover:shadow-md transition-shadow border-l-4 border-l-amber-400" data-testid="client-card-abacus-top">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-base font-semibold text-slate-900">Abacus Medicine</h2>
                <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700">Standing partner</Badge>
              </div>
              <p className="text-xs text-slate-500">EU specialty distribution · always-on commercial pipeline · shortlist + outreach tracker</p>
            </div>
            <button
              onClick={() => navigate('/abacus')}
              className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-md inline-flex items-center gap-1 shrink-0"
              data-testid="client-open-abacus-top"
            >
              Open <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </Card>

        {engagements.length === 0 ? (
          <Card className="p-10 text-center border-dashed">
            <Sparkles className="w-8 h-8 text-violet-400 mx-auto mb-3" />
            <p className="text-sm text-slate-700 font-medium">No engagement mandates yet.</p>
            <p className="text-xs text-slate-500 mt-2 max-w-md mx-auto">
              Click <span className="font-semibold text-violet-700">Seed Viatris</span> to bootstrap the Viatris × Mainland China engagement
              with a 250-asset auto-shortlist drawn from your production biotech assets, OR
              create a custom client via <span className="font-semibold">New Engagement</span>.
            </p>
          </Card>
        ) : (
          engagements.map(e => (
            <Card key={e.id} className="p-5 hover:shadow-md transition-shadow" data-testid={`engagement-card-${e.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-base font-semibold text-slate-900">{e.name}</h2>
                    <Badge variant="secondary" className="text-[10px]">{e.client_company}</Badge>
                  </div>
                  <p className="text-xs text-slate-500">
                    {e.country_focus} · cohort {e.cohort_size} · {e.cadence}
                    {e.counts && ` · ${e.counts.total} candidates (${e.counts.active_cohort} active, ${e.counts.pursue} pursue)`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => {
                      // Feb 2026 · Route Viatris engagement directly to
                      // its dedicated Workbench (the actual workspace,
                      // superseding the old generic
                      // /engagements/:id EngagementDetailPage which was
                      // deleted). Fall back to the legacy path only for
                      // any future non-Viatris engagements — the legacy
                      // route currently redirects to the Workbench too,
                      // so this is belt-and-braces.
                      if (e.id === 'eng-viatris-china') {
                        navigate('/clients/viatris?cohort=Cohort+1');
                      } else if (e.id === 'eng-modulus-animal-health') {
                        navigate('/clients/modulus');
                      } else {
                        navigate(`/engagements/${e.id}`);
                      }
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-sky-700 bg-sky-50 hover:bg-sky-100 rounded-md inline-flex items-center gap-1"
                    data-testid={`engagement-open-${e.id}`}
                  >
                    Open <ExternalLink className="w-3 h-3" />
                  </button>
                  {e.public_share_token && (
                    <button
                      onClick={() => copyShareLink(e.public_share_token)}
                      className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md inline-flex items-center gap-1"
                      data-testid={`engagement-copy-link-${e.id}`}
                      title="Copy public share link"
                    >
                      {copiedToken === e.public_share_token ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                      Share link
                    </button>
                  )}
                </div>
              </div>
              {e.public_share_token && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Public dashboard URL</p>
                  <code className="text-[11px] text-slate-600 break-all" data-testid={`engagement-share-url-${e.id}`}>
                    {window.location.origin}/engagement-status/{e.public_share_token}
                  </code>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
